import styled from '@emotion/styled';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { withErrorHandling } from '@selfserve/react-hocs';
import { BillingDetailsModal } from '../ui/billing-details-modal';
import { BillingDetailsLayout } from '../ui/billing-details-layout';
import {
  useCountryList,
  useCardFormik,
  FormValues,
} from '@selfserve/react-hooks';
import {
  Chargebee,
  apiStatuses,
  cardFormTypes,
  handleNoDataOnResponse,
} from '@selfserve/react-utils';
import {
  UserBillingInfo,
  useCreatePaymentIntent,
  useCreateCardWithIntent,
  useUpdateBillingAddress,
} from '@selfserve/react-common-data';

import { IContextData } from './account-layout';

export const StyledErrorContainer = styled('p')(
  ({ theme, withoutPadding }: { theme?: any; withoutPadding?: boolean }) => ({
    display: 'flex',
    padding: !withoutPadding ? theme.spacing(1) : 0,
    paddingLeft: 0,
    margin: 0,
    fontSize: theme.typography.pxToRem(14),
    color: 'rgba(225, 36, 55, 1)',
    '&.err-text': {
      paddingTop: 0,
      marginBottom: '32px',
    },
    span: {
      margin: '2px 0 0 4px',
    },
  })
);

interface IEditAccountData {
  values?: FormValues;
  handleApiError: (text: string) => void;
  cardComponents?: HTMLElement | undefined;
  isAdd?: boolean;
}

export const BillingDetails = withErrorHandling(({ openErrorModal }) => {
  const { t } = useTranslation();
  const { subscriptionOverviewData = {}, handleSubscriptionOverviewRequest } =
    useOutletContext<IContextData>() || {};
  const { billing_info }: { billing_info?: UserBillingInfo } =
    subscriptionOverviewData || {};
  const { countries } = useCountryList();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const country = countries.find(
    (country) => country.value === billing_info?.billing_country
  );
  const [activeFormSection, setActiveFormSection] = useState('');
  const { mutateAsync: createPaymentIntent } = useCreatePaymentIntent(0);
  const { mutateAsync: createCardWithIntent } = useCreateCardWithIntent();
  const { mutateAsync: updateBillingAddress } = useUpdateBillingAddress();

  const onSubmitAction = (
    values: FormValues,
    isSubmited: boolean,
    setIsSubmited: (value: boolean) => void,
    cardComponents: HTMLElement | undefined,
    handleApiError: (text: string) => void
  ) => {
    if (!isSubmited && !isLoading) {
      setIsLoading(true);
      clearApiError();
      setIsSubmited(true);
      switch (activeFormSection) {
        case cardFormTypes.ADD_PAYMENT_METHOD:
          onPaymentMethodChange({
            values,
            handleApiError,
            cardComponents,
            isAdd: true,
          });
          break;

        case cardFormTypes.CHANGE_PAYMENT_METHOD:
          onPaymentMethodChange({
            values,
            handleApiError,
            cardComponents,
          });

          break;

        case cardFormTypes.EDIT_BILLING_INFO:
          onBillingAddressChange({
            values,
            handleApiError,
          });
          break;

        default:
          setIsModalOpen(false);
          setIsLoading(false);
          handleReset();
          break;
      }
    }
  };

  const onBillingAddressChange = ({
    values,
    handleApiError,
  }: IEditAccountData) => {
    const { address, city, country, state, zipCode } = values || {};
    updateBillingAddress({
      address: address as string,
      city: city as string,
      country_code: country as string,
      state_code: state as string,
      zip_code: zipCode as string,
    })
      .then((data: any) => {
        if (data) {
          setIsLoading(false);
          const { status, data: response } = data;
          if (status === apiStatuses.CREATED) {
            handleSubscriptionOverviewRequest({});
            setIsModalOpen(false);
            setActiveFormSection('');
            handleReset();
          } else {
            const message = handleNoDataOnResponse(response?.params);
            handleApiError(message || '');
          }
        }
      })
      .catch((err: Error) => {
        handleApiError(err.message);
      });
  };

  const onPaymentMethodChange = ({
    values,
    handleApiError,
    cardComponents,
    isAdd,
  }: IEditAccountData) => {
    const {
      address,
      city,
      country,
      firstName,
      lastName,
      state,
      zipCode,
      vatNumber,
    } = values || {};
    clearApiError();
    const cbInstance = Chargebee.getInstance();
    const currentAddress = isAdd ? address : billing_info?.billing_addr1;
    const currentCity = isAdd ? city : billing_info?.billing_city;
    const currentState = isAdd
      ? state
        ? t(`states.${country}.${state}`)
        : ''
      : billing_info?.billing_state;
    const currentStateCode = state || '';
    const currentZipCode = isAdd ? zipCode : billing_info?.billing_zip;
    const currentCountry = t(
      `countries.${isAdd ? country : billing_info?.billing_country}`
    );
    const currentCountryCode = isAdd ? country : billing_info?.billing_country;
    const currentFirstName = isAdd ? firstName : billing_info?.firstName;
    const currentLastName = isAdd ? lastName : billing_info?.lastName;
    cbInstance
      .tokenize(cardComponents, {
        billingAddr1: currentAddress,
        billingCity: currentCity,
        billingState: currentState,
        billingStateCode: currentStateCode,
        billingZip: currentZipCode,
        billingCountry: currentCountry,
        firstName: currentFirstName,
        lastName: currentLastName,
      })
      .then(({ token: cbToken }: { token: string }) => {
        createPaymentIntent({})
          .then(({ status, data }) => {
            if (status === apiStatuses.CREATED) {
              cbInstance.load3DSHandler().then((threeDSHandler: any) => {
                threeDSHandler.setPaymentIntent(data?.payment_intent);
                threeDSHandler.handleCardPayment(
                  { cbToken },
                  {
                    success: function (intent: any) {
                      createCardWithIntent({
                        billing_info: {
                          address: currentAddress,
                          city: currentCity,
                          country_code: currentCountryCode,
                          state_code: currentStateCode,
                          zip_code: currentZipCode,
                        },
                        intent_id: data?.payment_intent?.id,
                        gw_token: intent.active_payment_attempt.id_at_gateway,
                        vat_number: vatNumber,
                      })
                        .then((data: any) => {
                          if (data) {
                            setIsLoading(false);
                            const { status, data: response } = data;
                            if (status === apiStatuses.SUCCESS) {
                              handleSubscriptionOverviewRequest({});
                              setIsModalOpen(false);
                              handleReset();
                            } else {
                              const message = handleNoDataOnResponse(
                                response?.message
                              );
                              handleApiError(
                                message ||
                                  (response?.params && response?.params[0]) ||
                                  ''
                              );
                            }
                          }
                        })
                        .catch((err) => {
                          const error =
                            err?.displayMessage || err?.error?.message_en;
                          handleApiError(error || '');
                        });
                    },
                    error: function (intent: any, error: Error) {
                      handleApiError(error.message);
                    },
                  }
                );
              });
            } else {
              const message = handleNoDataOnResponse(data?.message);
              handleApiError(message || '');
            }
          })
          .catch((err) => handleApiError(err.message || ''));
      })
      .catch((err: Error) => handleApiError(err.message || ''));
  };

  const handleModalOpen = (activeFormSection: string) => {
    setActiveFormSection(activeFormSection);
    setIsModalOpen(true);
  };

  const handleModalToggle = useCallback(
    () => () => {
      setIsModalOpen((value) => {
        if (value) {
          setIsLoading(false);
          handleReset();
          clearApiError();
        }
        return !value;
      });
    },
    []
  );

  const {
    formik,
    handleDropdownChange,
    handleCardInputsChange,
    handleCardInputsBlur,
    cardRef,
    apiError,
    clearApiError,
    handleReset,
  } = useCardFormik({ onSubmitAction, openErrorModal, activeFormSection });
  const { handleSubmit } = formik;

  useEffect(() => {
    if (apiError) setIsLoading(false);
  }, [apiError]);

  return (
    <>
      <BillingDetailsLayout
        subscriptionOverviewData={subscriptionOverviewData}
        country={country?.text || ''}
        handleModalOpen={handleModalOpen}
        cardApiError=""
        billingApiError=""
      />
      <BillingDetailsModal
        open={isModalOpen}
        formik={formik}
        handleDropdownChange={handleDropdownChange}
        handleCardInputsChange={handleCardInputsChange}
        handleCardInputsBlur={handleCardInputsBlur}
        forwardRef={cardRef}
        handleClose={handleModalToggle()}
        handleSubmit={handleSubmit}
        chargebeeInited={Chargebee.inited}
        activeFormSection={activeFormSection}
        isLoading={isLoading}
        apiError={apiError}
      />
    </>
  );
});
